import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { noop, getWindow, getIsMobile } from '../../../utils'
import BtnV2 from '../../BtnV2'
import Scrollbars from '../../Scrollbars'
import RenderToLayer from '../RenderToLayer'

import { CloseButton } from './CloseButton'
import { CLOSE_REASONS, MODAL_IGNORE_ATTRIBUTE, PADDING_CLASSES } from './constants'

import cm from './Modal.module.css'

/**
 * @property {boolean} noContentPadding - (@deprecated) Use `contentPadding="none"` instead.
 */

export default class Modal extends React.Component {
  static propTypes = {
    /** Array of props map which will be applied to Btn component. Custom components can be used in this array too. */
    buttons: PropTypes.array,
    /** The content to be displayed inside the modal. */
    children: PropTypes.any,
    /** An optional CSS class to apply additional styles or customize the component's appearance. */
    className: PropTypes.string,
    /** CSS class for the overlay. */
    overlayClassName: PropTypes.string,
    /** If true, the modal cannot be closed. */
    disableClose: PropTypes.bool,
    /** If true, the modal cannot be closed with the escape key. */
    disableEscapeClose: PropTypes.bool,
    /** If true, the modal cannot be closed by clicking on the overlay. */
    disableOverlayClose: PropTypes.bool,
    /** If true, the close button will not be displayed. */
    disableCloseButton: PropTypes.bool,
    /** Array of buttons to be displayed in the header. */
    headerButtons: PropTypes.array,
    /** The component to be used for the modal. */
    modalComponent: PropTypes.string,
    /** If true, the modal is in confirm mode. Header will have different styles */
    isConfirm: PropTypes.bool,
    /** @deprecated Use `contentPadding="none"` instead. */
    noContentPadding: PropTypes.bool,
    /** Padding variant for the content: 'default' = 8px 16px, 'none' = 0, 'medium' = 8px 24px */
    contentPadding: PropTypes.oneOf(['default', 'none', 'medium']),
    /** Callback function to be called when the modal is closed. */
    onClose: PropTypes.func,
    /** Callback function to be called when the modal is opened. */
    onOpen: PropTypes.func,
    /** Callback function to be called when a request to close the modal is made. */
    onRequestClose: PropTypes.func,
    /** If true, the modal is open. */
    open: PropTypes.bool,
    /** If true, the modal content is scrollable. */
    scrollable: PropTypes.bool,
    /** The title of the modal. */
    title: PropTypes.any,
    /** The width of the modal. For example: 460, 640, 900, 1200, 100%. By default modal width is 640px. */
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Array of events that are allowed to propagate. */
    allowPropagationEvents: PropTypes.array,
    /** Data test id for the closer button */
    'closer-data-test-id': PropTypes.string,
    /** Data test id for the modal*/
    'data-test-id': PropTypes.string,
    /** id for the modal aria label element*/
    'aria-labelledby': PropTypes.string,
  }

  static defaultProps = {
    buttons: [],
    headerButtons: [],
    modalComponent: 'div',
    onRequestClose: noop,
    onClose: noop,
    onOpen: noop,
  }

  state = { open: false, containerWidth: 0, containerNode: null }

  handleRef = (node) => {
    if (node !== null) {
      this.setState({ containerNode: node })
    }
  }

  static getDerivedStateFromProps(nextProps) {
    if ('open' in nextProps) {
      const { open } = nextProps
      return { open }
    }
    return null
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize, true)
  }

  handleResize = () => {
    if (this.state.containerNode) {
      this.setState({ containerWidth: this.state.containerNode.clientWidth })
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize, true)
  }

  componentDidUpdate(_, prevState) {
    if (this.state.open !== !prevState.open) {
      this.props[`on${this.state.open ? 'Open' : 'Close'}`]()
    }
    if (this.state.containerNode !== prevState.containerNode) {
      this.handleResize()
    }
  }

  close = (reason, e) => {
    if (this.props.disableClose) {
      return
    }

    if ('open' in this.props) {
      this.props.onRequestClose(reason, e)
    } else {
      this.setState({ open: false })
    }
  }

  open = () => this.setState({ open: true })

  handleClickAway = (e) => {
    if (e.target.closest(`[${MODAL_IGNORE_ATTRIBUTE}="true"]`)) {
      return
    }

    !this.props.disableOverlayClose && this.close(CLOSE_REASONS.CLICK_AWAY, e)
  }

  handleEscapeKeyDown = (e) => !this.props.disableEscapeClose && this.close(CLOSE_REASONS.ESC, e)

  render() {
    const { open } = this.state
    const {
      buttons,
      children,
      className,
      overlayClassName,
      disableClose,
      disableCloseButton,
      headerButtons,
      isConfirm,
      noContentPadding,
      contentPadding = 'default',
      scrollable,
      title,
      width,
      allowPropagationEvents,
      'closer-data-test-id': closerDataTestId,
      'data-test-id': dataTestId,
      'aria-labelledby': ariaLabelledBy,
    } = this.props

    const hasHeaderButtons = headerButtons.length > 0
    const hasHeader = Boolean(title || hasHeaderButtons)
    const hasCloseBtn = !disableClose && !disableCloseButton
    const hasButtons = buttons.length > 0
    const offsetY = hasButtons ? 160 : 112
    const body = children ? (
      <div className={!noContentPadding ? PADDING_CLASSES[contentPadding] : ''}>{children}</div>
    ) : null
    const isMobile = getIsMobile()

    return (
      <RenderToLayer
        className={cx('relative', cm.overlay, overlayClassName)}
        render={this.renderModal}
        open={Boolean(open)}
        onEscapeKeyDown={this.handleEscapeKeyDown}
        onClickAway={this.handleClickAway}
        allowPropagationEvents={allowPropagationEvents}
        closerDataTestId="coupon-modal-close-btn"
      >
        <div
          ref={this.handleRef}
          className={cx(isMobile ? cm.mobile : cm.modal, className)}
          style={{ width }}
          tabIndex={-1}
          data-test-id={dataTestId}
          aria-labelledby={ariaLabelledBy}
        >
          {hasCloseBtn && !hasHeaderButtons && (
            <CloseButton
              onClick={this.close}
              withHeaderButtons={hasHeaderButtons}
              dataTestId={closerDataTestId}
            />
          )}
          {hasHeader && (
            <div className={cx('p-y-xs p-x row center')}>
              <div className={cx('text-heading-2 col-auto', cm.title)}>{title}</div>
              {hasHeaderButtons && (
                <>
                  <div className="col row end">{headerButtons}</div>
                  {hasCloseBtn && (
                    <CloseButton
                      onClick={this.close}
                      withHeaderButtons={hasHeaderButtons}
                      dataTestId={closerDataTestId}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {body && (
            <div className={cx(hasHeader && !isConfirm && 'b-t')}>
              {scrollable ? (
                <Scrollbars autoHeight autoHeightMax={getWindow().innerHeight - offsetY}>
                  {body}
                </Scrollbars>
              ) : (
                body
              )}
            </div>
          )}

          {hasButtons && (
            <div className={cx('p-y-xs p-x b-t row', buttons.length > 1 ? 'between' : 'end')}>
              {buttons.map((button, i) =>
                React.isValidElement(button) ? (
                  React.cloneElement(button, { key: button.key ?? i })
                ) : (
                  <BtnV2 {...button} key={i} />
                ),
              )}
            </div>
          )}
        </div>
      </RenderToLayer>
    )
  }
}
